import {
    Box,
    Button,
    ButtonGroup,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Spinner,
    Text,
    Textarea,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { FunctionComponent, useEffect, useMemo } from "react";
import { EntityResponse, useClientEntity, useClientEntityDelete, useClientEntityUpdate } from "../../../../api";
import { ConfirmationDialog, ConfirmationDialogProps } from "../../../../components/ConfirmationDialog";
import { useForm } from "react-hook-form";
import { createPatch } from "rfc6902";
import { useEntityTreeContext } from "../EntityTreeContext";
import { useAreaPermissions } from "../../../../hooks";
import { EntitiesArea } from "../../../../auth";

// ── Section header ─────────────────────────────────────────────────────────────
const SectionHeader: FunctionComponent<{ title: string; description?: string }> = ({ title, description }) => (
    <Box mb={4}>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            {title}
        </Text>
        {description && (
            <Text fontSize="xs" color="gray.500" mt={0.5}>
                {description}
            </Text>
        )}
        <Divider mt={3} />
    </Box>
);

// ── Public component ──────────────────────────────────────────────────────────
export type EntitySettingsTabProps = {
    clientId: string;
    entityId: string;
};

export const EntitySettingsTab: FunctionComponent<EntitySettingsTabProps> = ({ clientId, entityId }) => {
    const entity = useClientEntity(clientId, entityId, {});

    if (!entity.data) {
        return (
            <Flex align="center" justify="center" py={12}>
                <Spinner />
            </Flex>
        );
    }

    return <EntitySettingsForm clientId={clientId} entity={entity.data} />;
};

// ── Form ──────────────────────────────────────────────────────────────────────
const EntitySettingsForm: FunctionComponent<{
    clientId: string;
    entity: EntityResponse;
}> = ({ clientId, entity }) => {
    const formValues = useMemo(
        () => ({
            name: entity.name,
            externalId: entity.externalId ?? "",
            ein: entity.ein ?? "",
            customField01: entity.customField01 ?? "",
            customField02: entity.customField02 ?? "",
            customField03: entity.customField03 ?? "",
            customField04: entity.customField04 ?? "",
            customField05: entity.customField05 ?? "",
            note: entity.note ?? "",
        }),
        [entity]
    );

    const areaPermissions = useAreaPermissions(EntitiesArea, clientId);

    const {
        setValue,
        reset,
        formState: { isDirty },
        register,
        handleSubmit,
    } = useForm();

    useEffect(() => {
        Object.entries(formValues).forEach(([key, value]) => {
            setValue(key, value);
        });
    }, [formValues, setValue]);

    const clientEntityUpdate = useClientEntityUpdate();

    const { onRefetch } = useEntityTreeContext();
    const entityDelete = useClientEntityDelete();
    const hasNoDescendants = !hasAnyDescendants(entity);
    const deleteConfirmation = useDisclosure();

    const deleteConfirmationDialog: ConfirmationDialogProps = {
        ...deleteConfirmation,
        title: `Delete ${entity.name}`,
        confirmLabel: "Delete",
        confirmColorScheme: "red",
        onConfirm: () => {
            entityDelete
                .mutateAsync({ clientId, entityId: entity.id })
                .then(() => {
                    onRefetch();
                    deleteConfirmation.onClose();
                })
                .catch(deleteConfirmation.onClose);
        },
        isDisabled: false,
    };

    const save = async (data: Record<string, string>) => {
        const patch = createPatch(formValues, data);
        const response = await clientEntityUpdate.mutateAsync({ clientId, entityId: entity.id, patch });

        reset({
            name: response.name,
            externalId: response.externalId ?? "",
            ein: response.ein ?? "",
            customField01: response.customField01 ?? "",
            customField02: response.customField02 ?? "",
            customField03: response.customField03 ?? "",
            customField04: response.customField04 ?? "",
            customField05: response.customField05 ?? "",
            note: response.note ?? "",
        });
    };

    return (
        <>
            <form onSubmit={handleSubmit(save)}>
                <VStack align="stretch" spacing={6} maxW="600px">
                    {/* Entity Info */}
                    <Box>
                        <SectionHeader title="Entity Info" description="Name, identifiers, and profile details." />
                        <VStack align="stretch" spacing={3}>
                            <FormControl isRequired>
                                <FormLabel>Name</FormLabel>
                                <Input placeholder="Entity name" {...register("name")} />
                            </FormControl>
                            <HStack align="end">
                                <FormControl>
                                    <FormLabel>External ID</FormLabel>
                                    <Input placeholder="Optional external identifier" {...register("externalId")} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>EIN</FormLabel>
                                    <Input placeholder="XX-XXXXXXX" {...register("ein")} />
                                </FormControl>
                            </HStack>
                        </VStack>
                    </Box>

                    {/* Custom Fields */}
                    <Box>
                        <SectionHeader title="Custom Fields" description="Arbitrary data fields for integration or organizational purposes." />
                        <VStack align="stretch" spacing={3}>
                            <FormControl>
                                <FormLabel>Custom Field 1</FormLabel>
                                <Input placeholder="" {...register("customField01")} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Custom Field 2</FormLabel>
                                <Input placeholder="" {...register("customField02")} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Custom Field 3</FormLabel>
                                <Input placeholder="" {...register("customField03")} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Custom Field 4</FormLabel>
                                <Input placeholder="" {...register("customField04")} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Custom Field 5</FormLabel>
                                <Input placeholder="" {...register("customField05")} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Note</FormLabel>
                                <Textarea placeholder="" {...register("note")} rows={3} />
                            </FormControl>
                        </VStack>
                    </Box>

                    {/* Save / Cancel */}
                    <Flex justify="flex-end">
                        <ButtonGroup>
                            <Button variant="ghost" onClick={() => reset(formValues)} isDisabled={!isDirty}>
                                Discard
                            </Button>
                            {areaPermissions.has(EntitiesArea.update) && (
                                <Button type="submit" colorScheme="blue" isDisabled={!isDirty} isLoading={clientEntityUpdate.isLoading}>
                                    Save changes
                                </Button>
                            )}
                        </ButtonGroup>
                    </Flex>

                    {/* Danger Zone */}
                    {areaPermissions.has(EntitiesArea.delete) && hasNoDescendants && (
                        <Box>
                            <SectionHeader title="Danger Zone" description="Irreversible actions that affect this entity." />
                            <Box borderWidth="1px" borderColor="red.200" borderRadius="md" p={4}>
                                <Flex align="center" gap={4} justify="space-between">
                                    <Box>
                                        <Text fontWeight="medium" fontSize="sm">
                                            Delete this entity
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            Permanently removes this entity. Cannot be undone.
                                        </Text>
                                    </Box>
                                    <Button colorScheme="red" variant="outline" size="sm" onClick={deleteConfirmation.onOpen}>
                                        Delete entity
                                    </Button>
                                </Flex>
                            </Box>
                        </Box>
                    )}
                </VStack>
            </form>

            <ConfirmationDialog {...deleteConfirmationDialog}>
                <p>
                    This will permanently delete <strong>{entity.name}</strong>. This cannot be undone.
                </p>
                <br />
                <p>
                    Are you <em>sure</em>?
                </p>
            </ConfirmationDialog>

        </>
    );
};

function hasAnyDescendants(entity: EntityResponse): boolean {
    if (entity.candidateCount > 0) return true;
    if (entity.children.length > 0) return true;
    return entity.children.map(c => hasAnyDescendants(c)).reduce((p, n) => p || n, false);
}
